import { create } from 'zustand';

export type AdminRole =
  | 'super_admin'
  /** Access comes only from ticked permission boxes — no role template. */
  | 'sub_admin'
  | 'ops'
  | 'finance'
  | 'moderator'
  | 'kyc_reviewer'
  | 'kyc_viewer'
  /** Agency partner login — sees only its own agency portal. */
  | 'agency';

export interface AdminProfile {
  id: string;
  email: string;
  name: string;
  role: AdminRole;
  /**
   * EFFECTIVE permissions (role template ∪ granted), from the API.
   *
   * These drive what the panel SHOWS — menu items, routes, action buttons.
   * They are never what makes an action safe: the API re-checks every one of
   * them on every request, so a hidden button and a hand-crafted fetch get the
   * same 403. Optional so a session persisted by an older build still loads
   * (it is refreshed from /admin/auth/me on boot).
   */
  permissions?: string[];
}

/** What POST /admin/auth/login returns. */
export interface AdminSession {
  admin: AdminProfile;
  /** Sent as `Authorization: Bearer <token>`; the only admin credential. */
  token: string;
  expiresAt: string;
}

interface AuthState {
  token: string | null;
  expiresAt: string | null;
  profile: AdminProfile | null;
  signIn: (session: AdminSession) => void;
  setProfile: (p: AdminProfile) => void;
  /** Forgets the session in this browser only. Use `signOut()` from
   *  ./signOut to also end it on the server. */
  logout: () => void;
  hasRole: (...roles: AdminRole[]) => boolean;
  /** True when the signed-in admin holds `permission`. super_admin always does. */
  can: (permission: string) => boolean;
  /** True when they hold at least one of these. Handy for a page that several
   *  permissions can open. */
  canAny: (...permissions: string[]) => boolean;
}

const STORAGE_KEY = 'maja.admin.auth';
// Before SEC-1 the panel stored just the admin id here and the API trusted it.
// It is no longer a credential; drop it so it doesn't linger.
const LEGACY_STORAGE_KEY = 'maja.admin.session';

interface PersistedSession {
  token: string | null;
  expiresAt: string | null;
  profile: AdminProfile | null;
}

const EMPTY: PersistedSession = { token: null, expiresAt: null, profile: null };

function load(): PersistedSession {
  try {
    localStorage.removeItem(LEGACY_STORAGE_KEY);
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return EMPTY;
    const s = JSON.parse(raw) as PersistedSession;
    // An expired session is useless; start at the login page instead of a 401.
    if (!s.token || !s.expiresAt || Date.parse(s.expiresAt) <= Date.now()) {
      localStorage.removeItem(STORAGE_KEY);
      return EMPTY;
    }
    return s;
  } catch {
    return EMPTY;
  }
}

function save(s: PersistedSession): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(s));
  } catch {
    /* storage blocked: the session lasts for this page only */
  }
}

const initial = load();

export const useAuth = create<AuthState>((set, get) => ({
  token: initial.token,
  expiresAt: initial.expiresAt,
  profile: initial.profile,
  signIn: ({ token, expiresAt, admin }) => {
    set({ token, expiresAt, profile: admin });
    save({ token, expiresAt, profile: admin });
  },
  setProfile: (profile) => {
    set({ profile });
    save({ token: get().token, expiresAt: get().expiresAt, profile });
  },
  logout: () => {
    set(EMPTY);
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* nothing stored */
    }
  },
  hasRole: (...roles) => {
    const r = get().profile?.role;
    return r ? roles.includes(r) : false;
  },
  can: (permission) => {
    const p = get().profile;
    if (!p) return false;
    // The super admin is never gated, here or in the API — a permission added
    // in a later release must not make the panel hide itself from them.
    if (p.role === 'super_admin') return true;
    return p.permissions?.includes(permission) ?? false;
  },
  canAny: (...permissions) => {
    const can = get().can;
    return permissions.some((x) => can(x));
  },
}));

/**
 * Subscribe to just the permission-relevant slice. Components use this so they
 * re-render the moment /admin/auth/me refreshes the profile (a super admin
 * changing your access takes effect on your next page load, not next login).
 */
export function usePermissions(): {
  can: (permission: string) => boolean;
  canAny: (...permissions: string[]) => boolean;
  role: AdminRole | null;
} {
  const profile = useAuth((s) => s.profile);
  const isSuper = profile?.role === 'super_admin';
  const held = profile?.permissions;
  const can = (permission: string) => isSuper || (held?.includes(permission) ?? false);
  return { can, canAny: (...p: string[]) => p.some(can), role: profile?.role ?? null };
}
